// Keep what someone is typing, across the redirects that are not their fault.
//
// W39.74. Measured on a real session loss (dev-auto-login rotates the session,
// which is what signing in on a second phone does to a couples app):
//
//     url after : /login?next=%2Fdiaries%2Fnew&reason=session-expired
//     localStorage: []   sessionStorage: []
//     VERDICT: THE WRITING IS GONE
//
// The redirect and the explanation were already right — that part had been
// fixed. What was missing is that the editor's text lives only in React state,
// so the redirect unmounts it. A person who loses their session halfway through
// a diary entry is told why, signs back in, and lands on an **empty editor**.
//
// `sessionStorage` and not `localStorage`: a draft belongs to this tab, and
// `localStorage` would leave other people's words on a shared device until
// something overwrote them. It also survives the sign-in round trip, which is a
// navigation within the same tab.
//
// **Read synchronously, in a lazy initialiser.** The first version seeded the
// value in an effect and restored it in a second effect keyed on the editor
// existing. `useEditor` with `immediatelyRender: false` returns `null` on the
// first render, so the restore could run before the draft had been read, see an
// empty string, and never run again — measured in a browser as
// 「stored draft: <p>W39.74 …</p>」, 「still stored: yes」, 「the editor shows: ""」.
// A draft that is stored but never restored is the same loss with an extra
// step, and an effect is the wrong tool for a value that is available
// immediately.
//
// Cleared on a **successful** save only. Clearing it on unmount would defeat the
// entire purpose — the unmount is the thing being survived.

import { useCallback, useState } from "react";

const PREFIX = "heshe.draft.";

function storage(): Storage | null {
  // Absent in a test renderer that has not stubbed it. A draft is a nicety; it
  // must never be the reason a form fails to mount.
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readDraft(key: string): string {
  try {
    return storage()?.getItem(PREFIX + key) ?? "";
  } catch {
    return "";
  }
}

export function clearDraft(key: string): void {
  try {
    storage()?.removeItem(PREFIX + key);
  } catch {
    /* nothing to clear */
  }
}

export interface Draft {
  /** The stored value, read once at mount. Empty when there is no draft. */
  value: string;
  /** Call on every change. An empty string removes the key. */
  save: (next: string) => void;
  /** Call once the value is safely persisted elsewhere. */
  clear: () => void;
}

export function useDraft(key: string): Draft {
  // Lazy: read on mount, not on every render, and not after the fact.
  const [value] = useState(() => readDraft(key));

  const save = useCallback(
    (next: string) => {
      try {
        if (next) storage()?.setItem(PREFIX + key, next);
        else storage()?.removeItem(PREFIX + key);
      } catch {
        /* full or disabled: the form still works, the draft just is not kept */
      }
    },
    [key],
  );

  const clear = useCallback(() => clearDraft(key), [key]);

  return { value, save, clear };
}

export interface JsonDraft<T> {
  /** The stored value, or `fallback` when there is none. */
  value: T;
  save: (next: T) => void;
  clear: () => void;
}

/**
 * A draft for a **form**, not a field.
 *
 * W39.76. Drafting only the long field of a form is the wrong half-fix: the
 * title is lost by exactly the same redirect, so a person who comes back finds
 * their paragraph back and their title gone, which is worse than losing both —
 * it looks like the save half-worked. So the whole form travels as one value.
 *
 * Falls back to the store, not to a throw, and re-reads nothing: a draft that
 * fails to parse is a draft from an older shape of this form, and the right
 * answer to that is the server's copy, not an exception on mount.
 */
export function useJsonDraft<T>(key: string, fallback: T): JsonDraft<T> {
  const [value] = useState<T>(() => {
    const raw = readDraft(key);
    if (!raw) return fallback;
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? ({ ...fallback, ...parsed } as T) : fallback;
    } catch {
      return fallback;
    }
  });

  const save = useCallback(
    (next: T) => {
      try {
        storage()?.setItem(PREFIX + key, JSON.stringify(next));
      } catch {
        /* a draft is a nicety; the form still works without it */
      }
    },
    [key],
  );

  const clear = useCallback(() => clearDraft(key), [key]);

  return { value, save, clear };
}
